/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import * as d3 from "d3";
import { WaveshaperOutput } from "@wbaudio/utils";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { LinesLayer } from "../../layers/lines.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { SampleDomain } from "../../utils/sample.ts";
import { ChartPalette } from "../../utils/theme.ts";
import { CreateTransferChart } from "./transferChart.ts";

export interface WaveshaperDriveParam {
  value: number;
  min: number;
  max: number;
}

export interface WaveshaperTransferCurveProps {
  drive: WaveshaperDriveParam;
  curveAmount: number;
  transfer?: (inputNorm: number) => number;
  color?: string;
  onDragDrive?: (drive: number) => void;
}

function CreateWaveshaperChart(
  container: HTMLElement,
  mode: ChartMode,
  drive: { min: number; max: number },
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const curve = LinesLayer();
  const handles = mode === "editor"
    ? HandlesLayer({ x: d3.scaleLinear().domain([drive.min, drive.max]), onDrag })
    : null;
  const chart = CreateTransferChart(container, {
    mode,
    domain: [-1, 1],
    layers: [curve, ...(handles ? [handles] : [])],
  });
  return { chart, curve, handles, destroy: chart.destroy };
}

export function WaveshaperTransferCurve(props: WaveshaperTransferCurveProps) {
  const mode: ChartMode = props.onDragDrive ? "editor" : "inline";
  const onDrag = useLatest(props.onDragDrive);
  const { min, max, value } = props.drive;
  const color = props.color ?? ChartPalette.orange;
  const { ref, chart } = useChart(
    (container) =>
      CreateWaveshaperChart(
        container,
        mode,
        { min, max },
        (_, p) => onDrag.current?.(parseFloat(p.x.toFixed(3))),
      ),
    [mode, min, max],
  );

  useEffect(() => {
    if (!chart) return;
    const transfer = props.transfer ??
      ((input: number) => WaveshaperOutput(input, value, props.curveAmount));
    chart.curve.set([{ id: "curve", color, points: SampleDomain(transfer, [-1, 1], 64) }]);
    chart.handles?.set([
      {
        id: "drive",
        x: value,
        y: 0,
        axis: "x",
        guide: "x",
        color,
        label: `${(value * 100).toFixed(0)}%`,
      },
    ]);
    chart.chart.render();
  }, [chart, value, props.curveAmount, props.transfer, color]);

  return <div ref={ref} />;
}
